/**
 * Error handling utilities for G2M Portal
 * Provides centralized error logging and monitoring
 */

/**
 * Log error to external service (Sentry, LogRocket, etc.)
 * Currently logs to console in development, can be extended for production monitoring
 */
export const logErrorToService = (errorData) => {
  const enhancedErrorData = {
    ...errorData,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    // Add user context if available
    userId: localStorage.getItem('auth_token') ? 'authenticated' : 'anonymous',
  };

  // In development, log to console
  if (process.env.NODE_ENV === 'development') {
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', enhancedErrorData.error);
    console.error('Error Info:', enhancedErrorData.errorInfo);
    console.error('Context:', enhancedErrorData);
    console.groupEnd();
  }

  // In production, send to monitoring service
  if (process.env.NODE_ENV === 'production') {
    // TODO: Replace with your monitoring service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(errorData.error, { extra: enhancedErrorData });
    console.error('Production Error:', enhancedErrorData);
  }
};

/**
 * Get user-friendly error message based on error type
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred.';

  // Network errors
  if (error.message?.includes('fetch') || error.message?.includes('network')) {
    return 'Unable to connect to our servers. Please check your internet connection and try again.';
  }

  // Authentication errors
  if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
    return 'Your session has expired. Please sign in again.';
  }

  // Permission errors
  if (error.message?.includes('403') || error.message?.includes('forbidden')) {
    return 'You don\'t have permission to access this resource.';
  }

  // Server errors
  if (error.message?.includes('500') || error.message?.includes('server')) {
    return 'Our servers are experiencing issues. Please try again in a few minutes.';
  }

  // Default message
  return 'Something went wrong. Our team has been notified and is working on a fix.';
};

/**
 * Error boundary reset actions
 */
export const errorBoundaryResetKeys = {
  ROUTE_CHANGE: 'route-change',
  USER_ACTION: 'user-action',
  RETRY: 'retry',
};

/**
 * Check if error should trigger a page reload
 */
export const shouldReloadOnError = (error) => {
  // Reload for critical system errors
  const criticalErrors = [
    'ChunkLoadError',
    'Loading chunk',
    'Loading CSS chunk',
  ];

  return criticalErrors.some(criticalError => 
    error?.message?.includes(criticalError) || 
    error?.name?.includes(criticalError)
  );
};